<template>
    <Modal :show="show" @close="$emit('close')" custom_dialog_css="justify-content: center; max-width: 1000px; width: 1000px;">
        <template #header>
            <div class="modal-header">
                <div>
                    <h5 class="modal-title">{{ entityName }} ({{ isCompany ? 'Klant' : 'Locatie' }}) - Plechtigheden {{ filters?.ceremonyFrom ? `van ${formatDateTime(filters?.ceremonyFrom, 'DD-MM-YYYY')}` : '' }} {{ filters?.ceremonyTo ? `t/m ${formatDateTime(filters?.ceremonyTo, 'DD-MM-YYYY')}` : '' }}</h5>
                    <div class="d-flex w-100 mt-2 mb-2">
                        <div class="mr-auto">Totaal aantal plechtigheden: <strong>{{ ceremonies.total || 0 }}</strong></div>
                    </div>
                </div>
                <button type="button" class="close" @click="$emit('close')">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
        </template>
        <template #body>
            <FullscreenLoading v-if="isLoading" />

            <div v-else>
                <table v-if="ceremonies?.data?.length > 0" class="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Datum Plechtigheid</th>
                            <th>Code</th>
                            <th>Naam</th>
                            <th>Duur</th>
                            <th v-if="isCompany">Locatie</th>
                            <th v-if="isLocation">Klant</th>
                            <th>Aangemaakt op</th>
                            <th>Aangemaakt door</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="ceremony in ceremonies.data" :key="ceremony.id">
                            <td>{{ ceremony.id }}</td>
                            <td>{{ !ceremony.date_unknown ? formatDateTime(ceremony.date) : 'Geen datum' }}</td>
                            <td>
                                <a :href="ceremony.link" target="_blank">{{ ceremony.code }}</a>
                            </td>
                            <td>{{ ceremony.name }}</td>
                            <td>{{ $root.minutesToTimeInput(ceremony.duration) }}</td>
                            <td v-if="isCompany">{{ ceremony.location.name }}</td>
                            <td v-if="isLocation">{{ ceremony.company.name }}</td>
                            <td>{{ formatDateTime(ceremony.created_at, 'DD-MM-YYYY') }}</td>
                            <td>{{ ceremony.created_by === 'user' ? ceremony?.user?.email ?? '-' : '' }}</td>
                        </tr>
                    </tbody>
                </table>
                <div v-else class="text-center alert alert-warning">
                    <p class="mb-0">Geen plechtigheden gevonden.</p>
                </div>
                <Pagination :pagination="ceremonies" @change="getCeremonies" />
            </div>
        </template>
        <template #footer>
            <button type="button" class="btn btn-secondary" @click="$emit('close')">Sluit</button>
        </template>
    </Modal>
</template>

<script setup>
    import { notifyError } from '../../../../utils/notify.js';
    import { inject, ref, watch, defineEmits, defineProps, computed } from 'vue';
    import Pagination from '../../../../components/Pagination.vue';
    import Modal from '../../../../components/Modal.vue';
    import { formatDateTime } from '../../../../utils/formatDate.js';
    import FullscreenLoading from '../../../../components/FullscreenLoading.vue';

    const http = inject('http');

    const emits = defineEmits(['close']);

    const props = defineProps({
        show: {
            type: Boolean,
            default: false,
        },
        entityId: {
            type: [Number, String],
            default: null,
        },
        entityType: {
            type: String,
            default: 'company',
            validator: (value) => ['company', 'locations'].includes(value),
        },
        entityName: {
            type: String,
            required: true,
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
    });

    const isLoading = ref(false);
    const ceremonies = ref({});

    watch(() => props.show, (newValue) => {
        if (newValue && props.entityId) {
            getCeremonies(1);
        }
    });

    const isCompany = computed(() => props.entityType === 'company');
    const isLocation = computed(() => props.entityType === 'locations');

    async function getCeremonies(page = 1) {
        if (!props.entityId) {
            return;
        }

        isLoading.value = true;

        try {
            const endpoint = props.entityType === 'company'
                ? `/api/admin/companies/${props.entityId}/ceremonies`
                : `/api/admin/locations/${props.entityId}/ceremonies`;

            const response = await http.get(endpoint, {
                params: {
                    page: page,
                    ceremony_from: props.filters.ceremonyFrom,
                    ceremony_to: props.filters.ceremonyTo,
                },
            });

            ceremonies.value = response.data;
        } catch (error) {
            notifyError(error, 'De plechtigheden konden niet worden opgehaald');
        } finally {
            isLoading.value = false;
        }
    }
</script>
